import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BeaconFundRequestModal } from "./BeaconFundRequestModal";
import { CreateBeaconFundRequestModal } from "./CreateBeaconFundRequestModal";

// Hooks
import { useBeaconFilters } from "./hooks/useBeaconFilters";
import { useBeaconData } from "./hooks/useBeaconData";

// Components
import { FilterBar } from "./components/FilterBar";
import { TotalStatsCards, YearStatsCards } from "./components/StatsCards";

// Views
import { MonthlySummaryView } from "./views/MonthlySummaryView";

// Tables
import { BeaconFundRequestsTable } from "./tables/FundRequestsTable";
import { BeaconAllocationsTable } from "./tables/AllocationsTable";
import { CustomerSummaryTable } from "./tables/CustomerSummaryTable";
import { EmployeeSummaryTable } from "./tables/EmployeeSummaryTable";

// Utils
import {
  calculateCustomerSummaries,
  calculateEmployeeSummaries,
  calculateMonthlySummaries
} from "./utils/summaryCalculations";

export function BeaconFundDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setTab] = useState<"fund_requests" | "allocations" | "customer_summary" | "employee_summary" | "monthly_summary">(searchParams.get("tab") as any || 'fund_requests');
  
  // Use custom hooks
  const {
    selectedYears,
    setSelectedYears,
    selectedCustomerId,
    setSelectedCustomerId,
    selectedEmployeeId,
    setSelectedEmployeeId,
    yearOptions,
    filters,
  } = useBeaconFilters();

  const {
    users,
    customers,
    fundRequests,
    allocations,
    yearStats,
    allStats,
    isLoading,
    hasError,
    isSummaryLoading,
    isFundRequestsLoading,
    isAllocationsLoading,
    minimalUserListResult,
    customersResult,
  } = useBeaconData(filters);

  // Calculate summaries using utility functions
  const customerSummaries = useMemo(() => 
    calculateCustomerSummaries(fundRequests, allocations, selectedYears),
    [fundRequests, allocations, selectedYears]
  );

  const employeeSummaries = useMemo(() => 
    calculateEmployeeSummaries(fundRequests, allocations, selectedYears),
    [fundRequests, allocations, selectedYears]
  );

  const monthlySummaries = useMemo(() => 
    calculateMonthlySummaries(allocations, fundRequests, selectedYears),
    [allocations, fundRequests, selectedYears]
  );

  // Handle tab change
  const onTabChange = (tab: string) => {
    setTab(tab as any);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      return newParams;
    });
  };


  return (
    <div className="h-screen flex flex-col p-4">
      {/* Compact Header with Filters */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Beacon Fund Dashboard</h1>
            <p className="text-sm text-muted-foreground">Manage fund requests and track allocations</p>
          </div>
          <FilterBar
            selectedYears={selectedYears}
            setSelectedYears={setSelectedYears}
            selectedCustomerId={selectedCustomerId}
            setSelectedCustomerId={setSelectedCustomerId}
            selectedEmployeeId={selectedEmployeeId}
            setSelectedEmployeeId={setSelectedEmployeeId}
            yearOptions={yearOptions}
            customers={customers}
            users={users}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <TotalStatsCards allStats={allStats} fundRequests={fundRequests} selectedYears={selectedYears} isSummaryLoading={isSummaryLoading} />
      <YearStatsCards yearStats={yearStats} fundRequests={fundRequests} selectedYears={selectedYears} isSummaryLoading={isSummaryLoading} />

      {/* Main Content - Use remaining height */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={currentTab} onValueChange={onTabChange} className="h-full flex flex-col">
          <TabsList className="grid grid-cols-5 w-[750px] mb-4">
            <TabsTrigger value="fund_requests">Fund Requests</TabsTrigger>
            <TabsTrigger value="allocations">Allocations</TabsTrigger>
            <TabsTrigger value="customer_summary">Customer Summary</TabsTrigger>
            <TabsTrigger value="employee_summary">Employee Summary</TabsTrigger>
            <TabsTrigger value="monthly_summary">Monthly Summary</TabsTrigger>
          </TabsList>

          <TabsContent value="fund_requests" className="flex-1 overflow-hidden mt-0">
            <BeaconFundRequestsTable fundRequests={fundRequests} isLoading={isFundRequestsLoading} />
          </TabsContent>
          
          <TabsContent value="allocations" className="flex-1 overflow-hidden mt-0">
            <BeaconAllocationsTable allocations={allocations} selectedYears={selectedYears} isLoading={isAllocationsLoading} />
          </TabsContent>
          
          <TabsContent value="customer_summary" className="flex-1 overflow-hidden mt-0">
            <CustomerSummaryTable summaries={customerSummaries} isLoading={isFundRequestsLoading || isAllocationsLoading} />
          </TabsContent>
          
          <TabsContent value="employee_summary" className="flex-1 overflow-hidden mt-0">
            <EmployeeSummaryTable summaries={employeeSummaries} isLoading={isFundRequestsLoading || isAllocationsLoading} />
          </TabsContent>
          
          <TabsContent value="monthly_summary" className="flex-1 overflow-hidden mt-0">
            <MonthlySummaryView summaries={monthlySummaries} />
          </TabsContent>
        </Tabs>
      </div>
     <BeaconFundRequestModal />
     <CreateBeaconFundRequestModal />
    </div>
  );
}
